import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  RefreshCw, 
  Users, 
  UserPlus, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Eye,
  FileText,
  User,
  Calendar,
  MapPin,
  Loader2,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  RotateCcw,
  Volume2,
  ExternalLink,
  Trash2,
  Download,
  Link2,
  ThumbsUp,
  ThumbsDown,
  UserCheck,
  Edit,
  ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodPantryQueuePage = () => {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [summary, setSummary] = useState({});
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Location tab (default to Nashville)
  const [activeLocationTab, setActiveLocationTab] = useState('fp-nashville');
  
  // Filters
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');

  // Detail dialog
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [checkinDetail, setCheckinDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [staffNotes, setStaffNotes] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Delete confirmation
  const [deleteCheckinId, setDeleteCheckinId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Report dialog
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  // Manual verification states
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [participantSearchResults, setParticipantSearchResults] = useState([]);
  const [searchingParticipants, setSearchingParticipants] = useState(false);
  const [selectedParticipantToLink, setSelectedParticipantToLink] = useState(null);
  const [linkingParticipant, setLinkingParticipant] = useState(false);
  
  // Deny dialog states
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  
  // Completion restriction dialog states
  const [showCompletionBlockedDialog, setShowCompletionBlockedDialog] = useState(false);
  const [completionBlockedInfo, setCompletionBlockedInfo] = useState(null);
  const [forceCompleteReason, setForceCompleteReason] = useState('');
  const [denialReason, setDenialReason] = useState('');
  const [denyLoading, setDenyLoading] = useState(false);

  // Change client type states
  const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false);
  const [changeTypeLoading, setChangeTypeLoading] = useState(false);
  const [changeTypeError, setChangeTypeError] = useState(null);

  // Check if user can delete (system_admin, executive, operations_manager)
  const canDelete = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);

  const fetchQueue = useCallback(async (showLoader = true) => {
    if (showLoader) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      // Always filter by the active location tab
      params.append('location_id', activeLocationTab);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      params.append('date', selectedDate);

      const response = await axios.get(`${API}/food-pantry/queue?${params.toString()}`);
      setCheckins(response.data.checkins || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Failed to fetch queue:', error);
      toast.error('Failed to load check-in queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeLocationTab, selectedStatus, selectedDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/food-pantry/locations`);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchQueue(false), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchQueue]);

  const fetchCheckinDetail = async (checkinId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/food-pantry/queue/${checkinId}`);
      setCheckinDetail(response.data);
    } catch (error) {
      toast.error('Failed to load check-in details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleViewDetail = (checkin) => {
    setSelectedCheckin(checkin);
    setStaffNotes(checkin.staff_notes || '');
    fetchCheckinDetail(checkin.id);
    setShowDetailDialog(true);
  };

  const handleCallNowServing = async (checkin) => {
    try {
      // Get the location's operational mode
      const location = locations.find(loc => loc.id === checkin.location_id);
      const operationalMode = location?.operational_mode || 
        (checkin.location_id === 'fp-charlotte' ? 'charlotte' : 'nashville');
      
      // Charlotte Mode has different behavior - no "Now Serving" message, just staff alert
      if (operationalMode === 'charlotte') {
        // For Charlotte Mode: Add to lobby queue (triggers staff alert) but DON'T mark as completed
        // Participant stays visible on lobby display until manually marked complete
        try {
          await axios.post(`${API}/food-pantry/charlotte-lobby-queue/add`, {
            checkin_id: checkin.id
          });
          
          // Update status to "Called" (not "Completed") - keeps them in queue and on lobby display
          await axios.put(`${API}/food-pantry/queue/${checkin.id}`, {
            queue_status: 'Called'
          });
          
          toast.success(`#${checkin.queue_number || ''} ${checkin.first_name} - Called (Staff Alert Sent)`);
          fetchQueue(false);
        } catch (e) {
          console.error('Failed to call participant (Charlotte Mode):', e);
          toast.error(e.response?.data?.detail || 'Failed to call participant');
        }
        return;
      }
      
      // Nashville Mode behavior: Display "Now Serving" message and mark as completed
      let lobbyMessage = "Please meet us at the door to have assistance taking your box to your car";
      try {
        const settingsResponse = await axios.get(`${API}/admin/food-pantry/settings`);
        if (settingsResponse.data?.settings?.lobby_display_message) {
          lobbyMessage = settingsResponse.data.settings.lobby_display_message;
        }
      } catch (e) {
        // Use default message if settings fetch fails
      }

      // Display the now serving alert (Nashville Mode only)
      await axios.post(`${API}/food-pantry/now-serving`, {
        location_id: checkin.location_id || 'fp-nashville',
        checkin_id: checkin.id,
        queue_number: checkin.queue_number || 0,
        message: lobbyMessage
      });
      
      // Mark as completed (Nashville Mode only)
      await axios.put(`${API}/food-pantry/queue/${checkin.id}`, {
        queue_status: 'Completed',
        box_distributed: true
      });
      
      toast.success(`#${checkin.queue_number || ''} ${checkin.first_name} - Called & Completed`);
      fetchQueue(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete action');
    }
  };

  const openLobbyDisplay = () => {
    // Get the active location's operational mode
    const location = locations.find(loc => loc.id === activeLocationTab);
    const operationalMode = location?.operational_mode || 
      (activeLocationTab === 'fp-charlotte' ? 'charlotte' : 'nashville');
    const lobbyUrl = operationalMode === 'charlotte' ? '/lobby-display-charlotte' : '/lobby-display';
    window.open(lobbyUrl, '_blank', 'noopener,noreferrer');
  };

  const handleUpdateStatus = async (status, additionalData = {}) => {
    if (!selectedCheckin) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/food-pantry/queue/${selectedCheckin.id}`, {
        queue_status: status,
        notes: staffNotes || undefined,
        ...additionalData
      });
      toast.success('Check-in updated successfully');
      setShowDetailDialog(false);
      fetchQueue();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Handle completion restriction errors
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error_type) {
        if (errorDetail.error_type === 'not_yet_eligible') {
          // Show dialog with override option
          setCompletionBlockedInfo({
            type: 'not_yet_eligible',
            message: errorDetail.message,
            eligibleDate: errorDetail.eligible_date,
            eligibilityWindow: errorDetail.eligibility_window,
            canOverride: errorDetail.can_override,
            instruction: errorDetail.override_instruction
          });
          setShowCompletionBlockedDialog(true);
        } else if (errorDetail.error_type === 'needs_resolution') {
          // Show dialog without override option
          setCompletionBlockedInfo({
            type: 'needs_resolution',
            message: errorDetail.message,
            resolutionNeeded: errorDetail.resolution_needed,
            canOverride: errorDetail.can_override
          });
          setShowCompletionBlockedDialog(true);
        } else {
          toast.error(errorDetail.message || 'Failed to update check-in');
        }
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to update check-in');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceComplete = async () => {
    if (!selectedCheckin || !forceCompleteReason.trim()) {
      toast.error('Please provide a reason for the override');
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API}/food-pantry/queue/${selectedCheckin.id}`, {
        queue_status: 'Completed',
        box_distributed: true,
        notes: staffNotes || undefined,
        force_complete_override: true,
        force_complete_reason: forceCompleteReason
      });
      toast.success('Check-in marked complete with override');
      setShowCompletionBlockedDialog(false);
      setShowDetailDialog(false);
      setForceCompleteReason('');
      setCompletionBlockedInfo(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete with override');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOverride = async (overrideStatus) => {
    if (!selectedCheckin || !overrideReason.trim()) {
      toast.error('Please provide a reason for the override');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.put(`${API}/food-pantry/queue/${selectedCheckin.id}`, {
        override_status: overrideStatus,
        override_reason: overrideReason,
        box_distributed: overrideStatus === 'Eligible',
        notes: staffNotes || undefined
      });
      
      // Update the local state with the returned check-in data for immediate UI update
      if (response.data.checkin) {
        setSelectedCheckin(response.data.checkin);
        if (checkinDetail) {
          setCheckinDetail({
            ...checkinDetail,
            checkin: response.data.checkin
          });
        }
      }
      
      toast.success('Override applied successfully');
      setShowDetailDialog(false);
      setOverrideReason('');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply override');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryNote = async () => {
    if (!selectedCheckin) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/food-pantry/queue/${selectedCheckin.id}/retry-note`);
      toast.success('Case note created successfully');
      fetchCheckinDetail(selectedCheckin.id);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (checkinId) => {
    setDeleteCheckinId(checkinId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCheckinId) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/food-pantry/queue/${deleteCheckinId}`);
      toast.success('Check-in entry deleted successfully');
      setShowDeleteDialog(false);
      setDeleteCheckinId(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete check-in entry');
    } finally {
      setDeleteLoading(false);
    }
  };

  // === Manual Verification Handlers ===
  
  const handleSearchParticipants = async () => {
    if (!participantSearchQuery.trim()) return;
    setSearchingParticipants(true);
    try {
      const response = await axios.get(`${API}/participants`, {
        params: { search: participantSearchQuery, limit: 20 }
      });
      setParticipantSearchResults(response.data.items || response.data.participants || []);
    } catch (error) {
      toast.error('Failed to search participants');
      setParticipantSearchResults([]);
    } finally {
      setSearchingParticipants(false);
    }
  };

  const handleOpenLinkDialog = () => {
    setParticipantSearchQuery('');
    setParticipantSearchResults([]);
    setSelectedParticipantToLink(null);
    // Pre-fill search with check-in name
    if (checkinDetail?.checkin) {
      const name = `${checkinDetail.checkin.first_name} ${checkinDetail.checkin.last_name}`;
      setParticipantSearchQuery(name);
    }
    setShowLinkDialog(true);
  };

  const handleLinkParticipant = async () => {
    if (!selectedCheckin || !selectedParticipantToLink) return;
    setLinkingParticipant(true);
    try {
      const response = await axios.post(`${API}/food-pantry/queue/${selectedCheckin.id}/link-participant`, {
        participant_id: selectedParticipantToLink.id
      });
      toast.success(`Check-in linked to ${response.data.participant?.full_name || 'participant'}`);
      setShowLinkDialog(false);
      setSelectedParticipantToLink(null);
      // Refresh detail
      fetchCheckinDetail(selectedCheckin.id);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link participant');
    } finally {
      setLinkingParticipant(false);
    }
  };

  const handleApproveVerification = async () => {
    if (!selectedCheckin) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/food-pantry/queue/${selectedCheckin.id}/approve`, {
        notes: staffNotes || undefined
      });
      if (response.data.case_note_created) {
        toast.success('Check-in approved and case note created');
      } else if (response.data.case_note_error) {
        toast.warning(`Check-in approved, but case note failed: ${response.data.case_note_error}`);
      } else {
        toast.success('Check-in approved');
      }
      setShowDetailDialog(false);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve check-in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenDenyDialog = () => {
    setDenialReason('');
    setShowDenyDialog(true);
  };

  const handleDenyVerification = async () => {
    if (!selectedCheckin) return;
    setDenyLoading(true);
    try {
      await axios.post(`${API}/food-pantry/queue/${selectedCheckin.id}/deny`, {
        reason: denialReason || undefined,
        notes: staffNotes || undefined
      });
      toast.success('Check-in denied and marked as ineligible');
      setShowDenyDialog(false);
      setShowDetailDialog(false);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny check-in');
    } finally {
      setDenyLoading(false);
    }
  };

  // Handle changing check-in client type
  const handleChangeClientType = async (newType) => {
    if (!selectedCheckin) return;
    
    setChangeTypeLoading(true);
    setChangeTypeError(null);
    
    try {
      const response = await axios.put(`${API}/food-pantry/queue/${selectedCheckin.id}/change-type`, {
        new_type: newType
      });
      
      if (response.data.success) {
        if (response.data.changed) {
          if (newType === 'returning') {
            toast.success(`Converted to Returning Client${response.data.auto_note_created ? ' - Case note created automatically' : ''}`);
          } else {
            toast.success('Converted to New Client');
          }
        } else {
          toast.info(response.data.message);
        }
        setShowChangeTypeDialog(false);
        setShowDetailDialog(false);
        fetchQueue();
      } else {
        // Eligibility check failed
        setChangeTypeError(response.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to change client type';
      setChangeTypeError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setChangeTypeLoading(false);
    }
  };

  // Check if check-in requires manual verification
  const requiresManualVerification = (checkin) => {
    if (!checkin) return false;
    const manualStatuses = [
      'Manual Verification Required',
      'Participant Not Found',
      'Multiple Participant Matches',
      'No Prior Food Box Record Found'
    ];
    return manualStatuses.includes(checkin.queue_status) || 
           manualStatuses.includes(checkin.eligibility_status);
  };

  // Check if participant is linked
  const hasLinkedParticipant = (checkin) => {
    return checkin && checkin.matched_participant_id;
  };

  const getStatusBadge = (checkin) => {
    const status = checkin.queue_status;
    if (status === 'Completed') return <Badge className="bg-green-500">Completed</Badge>;
    if (status === 'Waiting') return <Badge className="bg-blue-500">Waiting</Badge>;
    if (status?.includes('Eligible')) return <Badge className="bg-emerald-500">{status}</Badge>;
    if (status?.includes('Not Yet')) return <Badge className="bg-orange-500">{status}</Badge>;
    if (status?.includes('Manual') || status?.includes('Verification')) return <Badge className="bg-yellow-500">{status}</Badge>;
    if (status?.includes('New Client')) return <Badge className="bg-purple-500">{status}</Badge>;
    if (status === 'Cancelled') return <Badge variant="secondary">Cancelled</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const getClientTypeBadge = (type) => {
    if (type === 'new') return <Badge variant="outline" className="border-purple-500 text-purple-600">New Client</Badge>;
    return <Badge variant="outline" className="border-emerald-500 text-emerald-600">Returning</Badge>;
  };

  // Format time to 12-hour format, optionally in Eastern Time for Charlotte
  const formatTime12Hour = (timeStr, useET = false) => {
    if (!timeStr) return '-';
    try {
      if (useET || selectedLocation?.id === 'fp-charlotte') {
        // For Charlotte: Convert to Eastern Time
        const today = new Date().toISOString().split('T')[0];
        const fullDateTime = timeStr.includes('T') ? timeStr : `${today}T${timeStr}Z`;
        const date = new Date(fullDateTime);
        return date.toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
      // For Nashville: Keep existing behavior (local time)
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Format date/time to Eastern Time for Charlotte
  const formatDateTimeET = (dateTimeStr) => {
    if (!dateTimeStr) return '-';
    try {
      const date = new Date(dateTimeStr);
      if (selectedLocation?.id === 'fp-charlotte') {
        return date.toLocaleString('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }) + ' ET';
      }
      // Nashville uses local time
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateTimeStr;
    }
  };

  const filteredCheckins = checkins.filter(checkin => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const name = `${checkin.first_name} ${checkin.last_name}`.toLowerCase();
      if (!name.includes(query)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="food-pantry-queue-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Food Pantry Check-In Queue</h1>
          <p className="text-muted-foreground">Manage client check-ins and food box distribution</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReportDialog(true)}
            data-testid="generate-queue-report-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openLobbyDisplay}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Lobby Display
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Auto-Refresh ON
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Auto-Refresh OFF
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => fetchQueue()}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Location Tabs */}
      <Tabs value={activeLocationTab} onValueChange={setActiveLocationTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="fp-nashville" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Nashville
          </TabsTrigger>
          <TabsTrigger value="fp-charlotte" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Charlotte
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.waiting || 0}</p>
                <p className="text-xs text-muted-foreground">Waiting</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.eligible || 0}</p>
                <p className="text-xs text-muted-foreground">Eligible</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.needs_review || 0}</p>
                <p className="text-xs text-muted-foreground">Needs Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                <UserPlus className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.new_clients || 0}</p>
                <p className="text-xs text-muted-foreground">New Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="date" className="sr-only">Date</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Waiting">Waiting</SelectItem>
                <SelectItem value="Under Review">Under Review</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Manual Verification Required">Manual Verification</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredCheckins.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No check-ins found</h3>
              <p className="text-muted-foreground">
                {selectedDate === new Date().toISOString().split('T')[0] 
                  ? 'No clients have checked in yet today' 
                  : 'No check-ins found for the selected date'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-4 font-medium">Time</th>
                    <th className="text-left p-4 font-medium">Client</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Match Status</th>
                    <th className="text-left p-4 font-medium">Eligibility</th>
                    {activeLocationTab === 'fp-charlotte' && (
                      <th className="text-left p-4 font-medium">SNAP Outreach</th>
                    )}
                    <th className="text-left p-4 font-medium">Queue Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredCheckins.map(checkin => (
                    <tr key={checkin.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <span className="text-sm font-medium">
                          {formatTime12Hour(checkin.checkin_time?.substring(0, 5))}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{checkin.first_name} {checkin.last_name}</div>
                        {checkin.matched_participant_masked && (
                          <div className="text-xs text-muted-foreground">
                            ID: {checkin.matched_participant_masked}
                          </div>
                        )}
                      </td>
                      <td className="p-4">{getClientTypeBadge(checkin.client_type)}</td>
                      <td className="p-4">
                        <span className={`text-sm ${
                          checkin.match_status === 'Confirmed' ? 'text-emerald-600' :
                          checkin.match_status === 'Multiple Participant Matches' ? 'text-orange-600' :
                          checkin.match_status === 'Participant Not Found' ? 'text-red-600' :
                          'text-muted-foreground'
                        }`}>
                          {checkin.match_status || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          {checkin.eligibility_status || '-'}
                          {checkin.monthly_return_date && (
                            <div className="text-xs text-muted-foreground">
                              Return: {checkin.monthly_return_date}
                            </div>
                          )}
                        </div>
                      </td>
                      {activeLocationTab === 'fp-charlotte' && (
                        <td className="p-4">
                          {checkin.client_type === 'returning' && checkin.snap_outreach_status ? (
                            <span className={`text-sm font-medium ${
                              checkin.has_snap_outreach === true ? 'text-emerald-600' : 
                              checkin.has_snap_outreach === false ? 'text-orange-600' : 'text-gray-600'
                            }`}>
                              {checkin.has_snap_outreach === true ? 'Yes' : 
                               checkin.has_snap_outreach === false ? 'No — Needs Intake' : '-'}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      )}
                      <td className="p-4">{getStatusBadge(checkin)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCallNowServing(checkin)}
                            className="border-green-500 text-green-600 hover:bg-green-50"
                            data-testid={`call-checkin-${checkin.id}`}
                          >
                            <Volume2 className="h-4 w-4 mr-1" />
                            Call
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetail(checkin)}
                            data-testid={`view-checkin-${checkin.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(checkin.id)}
                              className="border-red-500 text-red-600 hover:bg-red-50"
                              data-testid={`delete-checkin-${checkin.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Check-In Details</DialogTitle>
            <DialogDescription>
              Review and manage client check-in
            </DialogDescription>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : checkinDetail ? (
            <div className="space-y-6">
              {/* Client Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Client Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">
                      {checkinDetail.checkin.first_name} {checkinDetail.checkin.last_name}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Client Type:</span>
                    <div className="flex items-center gap-2">
                      <p className="font-medium capitalize">
                        {checkinDetail.checkin.client_type === 'new' ? 'New Client' : 'Returning Client'}
                      </p>
                      {checkinDetail.checkin.queue_status !== 'Completed' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setChangeTypeError(null);
                            setShowChangeTypeDialog(true);
                          }}
                          data-testid="btn-change-type"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Change
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Check-in Time:</span>
                    <p className="font-medium">
                      {checkinDetail.checkin.checkin_date} {formatTime12Hour(checkinDetail.checkin.checkin_time)}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Location:</span>
                    <p className="font-medium">{checkinDetail.checkin.location_name}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Eligibility Info */}
              {checkinDetail.checkin.client_type === 'returning' && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Eligibility Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Match Status:</span>
                      <span className={`font-medium ${
                        checkinDetail.checkin.match_status === 'Confirmed' ? 'text-emerald-600' : 'text-orange-600'
                      }`}>
                        {checkinDetail.checkin.match_status || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Eligibility Status:</span>
                      <span className="font-medium">{checkinDetail.checkin.eligibility_status}</span>
                    </div>
                    {checkinDetail.checkin.last_distribution_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Distribution:</span>
                        <span className="font-medium">{checkinDetail.checkin.last_distribution_date}</span>
                      </div>
                    )}
                    {checkinDetail.checkin.monthly_return_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Monthly Return Date:</span>
                        <span className="font-medium">{checkinDetail.checkin.monthly_return_date}</span>
                      </div>
                    )}
                    {checkinDetail.checkin.eligibility_window && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Eligibility Window:</span>
                        <span className="font-medium">{checkinDetail.checkin.eligibility_window}</span>
                      </div>
                    )}
                    {checkinDetail.checkin.notification_status && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Eligibility SMS:</span>
                        <span className={`font-medium ${
                          checkinDetail.checkin.notification_status === 'sent' ? 'text-green-600' :
                          checkinDetail.checkin.notification_status === 'scheduled' ? 'text-blue-600' :
                          checkinDetail.checkin.notification_status === 'pending_timezone' ? 'text-yellow-600' :
                          'text-gray-600'
                        }`}>
                          {checkinDetail.checkin.notification_status === 'sent' ? 'Sent' :
                           checkinDetail.checkin.notification_status === 'scheduled' ? `Scheduled for ${checkinDetail.checkin.notification_scheduled_at ? formatDateTime(checkinDetail.checkin.notification_scheduled_at) : 'pending'}` :
                           checkinDetail.checkin.notification_status === 'pending_timezone' ? 'Pending - Time Zone Required' :
                           checkinDetail.checkin.notification_status}
                        </span>
                      </div>
                    )}
                    {checkinDetail.checkin.notification_sent_at && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">SMS Sent:</span>
                        <span className="font-medium text-green-600">{formatDateTime(checkinDetail.checkin.notification_sent_at)}</span>
                      </div>
                    )}
                    {checkinDetail.checkin.eligibility_explanation && (
                      <div className="pt-2 border-t">
                        <span className="text-muted-foreground block mb-1">Explanation:</span>
                        <p className="text-sm">{checkinDetail.checkin.eligibility_explanation}</p>
                      </div>
                    )}
                    {/* SNAP Outreach Status - Charlotte only */}
                    {checkinDetail.checkin.snap_outreach_status && (
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">SNAP Outreach:</span>
                          <span className={`font-medium ${
                            checkinDetail.checkin.has_snap_outreach === true ? 'text-emerald-600' : 
                            checkinDetail.checkin.has_snap_outreach === false ? 'text-orange-600' : 'text-gray-600'
                          }`}>
                            {checkinDetail.checkin.has_snap_outreach === true ? 'Yes' : 
                             checkinDetail.checkin.has_snap_outreach === false ? 'No — Needs Intake' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Case Note Status */}
              {checkinDetail.checkin.client_type === 'returning' && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Case Note Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {checkinDetail.checkin.auto_note_created ? (
                      <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle className="h-5 w-5" />
                        <span>Returning Box Distribution note created automatically</span>
                      </div>
                    ) : checkinDetail.checkin.auto_note_error ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-red-600">
                          <XCircle className="h-5 w-5" />
                          <span>Case note creation failed: {checkinDetail.checkin.auto_note_error}</span>
                        </div>
                        {checkinDetail.checkin.matched_participant_id && checkinDetail.checkin.food_pantry_case_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRetryNote}
                            disabled={actionLoading}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Retry Note Creation
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <AlertCircle className="h-5 w-5" />
                        <span>No automatic case note for this check-in</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Manual Verification Section */}
              {requiresManualVerification(checkinDetail.checkin) && checkinDetail.checkin.queue_status !== 'Completed' && (
                <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-purple-700">
                      <UserCheck className="h-4 w-4" />
                      Manual Verification Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-purple-600">
                      This check-in requires staff verification before the client can be served.
                      {!hasLinkedParticipant(checkinDetail.checkin) && (
                        <span className="block mt-1 font-medium">
                          No participant is currently linked. Please search and link an existing participant.
                        </span>
                      )}
                    </p>

                    {/* Linked Participant Info */}
                    {hasLinkedParticipant(checkinDetail.checkin) && (
                      <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-700">Participant Linked</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <p><span className="text-muted-foreground">Name:</span> {checkinDetail.checkin.matched_participant_name}</p>
                          {checkinDetail.checkin.linked_by_name && (
                            <p><span className="text-muted-foreground">Linked by:</span> {checkinDetail.checkin.linked_by_name}</p>
                          )}
                          {checkinDetail.checkin.eligibility_status && (
                            <p><span className="text-muted-foreground">Eligibility:</span> {checkinDetail.checkin.eligibility_status}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Verification Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      {/* Link to Participant Button */}
                      <Button
                        variant="outline"
                        onClick={handleOpenLinkDialog}
                        disabled={actionLoading}
                        className="border-blue-500 text-blue-700 hover:bg-blue-50"
                        data-testid="link-participant-btn"
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        {hasLinkedParticipant(checkinDetail.checkin) ? 'Change Linked Participant' : 'Link to Existing Participant'}
                      </Button>

                      {/* Approve Button - only show if participant is linked */}
                      {hasLinkedParticipant(checkinDetail.checkin) && (
                        <Button
                          onClick={handleApproveVerification}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="approve-verification-btn"
                        >
                          {actionLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <ThumbsUp className="h-4 w-4 mr-2" />
                          )}
                          Approve
                        </Button>
                      )}

                      {/* Deny Button */}
                      <Button
                        variant="outline"
                        onClick={handleOpenDenyDialog}
                        disabled={actionLoading}
                        className="border-red-500 text-red-700 hover:bg-red-50"
                        data-testid="deny-verification-btn"
                      >
                        <ThumbsDown className="h-4 w-4 mr-2" />
                        Deny
                      </Button>
                    </div>

                    {/* Verification Status Info */}
                    {checkinDetail.checkin.verification_status === 'approved' && (
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded text-sm text-green-700">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Approved by {checkinDetail.checkin.approved_by_name} on {formatDateTime(checkinDetail.checkin.approved_at)}
                      </div>
                    )}
                    {checkinDetail.checkin.verification_status === 'denied' && (
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded text-sm text-red-700">
                        <XCircle className="h-4 w-4 inline mr-1" />
                        Denied by {checkinDetail.checkin.denied_by_name} on {formatDateTime(checkinDetail.checkin.denied_at)}
                        {checkinDetail.checkin.denial_reason && (
                          <p className="mt-1">Reason: {checkinDetail.checkin.denial_reason}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Staff Notes */}
              <div className="space-y-2">
                <Label htmlFor="staffNotes">Staff Notes</Label>
                <Textarea
                  id="staffNotes"
                  value={staffNotes}
                  onChange={(e) => setStaffNotes(e.target.value)}
                  placeholder="Add notes about this check-in..."
                  rows={3}
                />
              </div>

              {/* Override Section (for eligible override) */}
              {checkinDetail.checkin.eligibility_status?.includes('Not Yet') && (
                <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="h-4 w-4" />
                      Manager Override
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-orange-600">
                      This client is not yet eligible. You may override if there are extenuating circumstances.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="overrideReason">Override Reason (Required)</Label>
                      <Textarea
                        id="overrideReason"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Explain why this override is being applied..."
                        rows={2}
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => handleOverride('Eligible')}
                      disabled={actionLoading || !overrideReason.trim()}
                      className="border-orange-500 text-orange-700 hover:bg-orange-100"
                    >
                      Apply Override - Mark Eligible
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}

          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
            >
              Close
            </Button>
            {checkinDetail?.checkin.queue_status !== 'Completed' && (
              <>
                {checkinDetail?.checkin.queue_status !== 'Cancelled' && (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus('Cancelled')}
                    disabled={actionLoading}
                  >
                    Cancel Check-In
                  </Button>
                )}
                <Button
                  onClick={() => handleUpdateStatus('Completed', { box_distributed: true })}
                  disabled={actionLoading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Mark Completed
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Check-In Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this check-in entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteCheckinId(null);
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Blocked Dialog */}
      <Dialog open={showCompletionBlockedDialog} onOpenChange={(open) => {
        setShowCompletionBlockedDialog(open);
        if (!open) {
          setCompletionBlockedInfo(null);
          setForceCompleteReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Cannot Complete Check-In
            </DialogTitle>
          </DialogHeader>
          
          {completionBlockedInfo && (
            <div className="space-y-4">
              {/* Not Yet Eligible - with override option */}
              {completionBlockedInfo.type === 'not_yet_eligible' && (
                <>
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {completionBlockedInfo.message}
                    </p>
                    <div className="mt-3 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                      <p><span className="font-medium">Eligible Date:</span> {completionBlockedInfo.eligibleDate}</p>
                      {completionBlockedInfo.eligibilityWindow && (
                        <p><span className="font-medium">Return Window:</span> {completionBlockedInfo.eligibilityWindow}</p>
                      )}
                    </div>
                  </div>
                  
                  {completionBlockedInfo.canOverride && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {completionBlockedInfo.instruction}
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="force-complete-reason">Override Reason (Required)</Label>
                        <Textarea
                          id="force-complete-reason"
                          placeholder="Explain why this participant should receive service early..."
                          value={forceCompleteReason}
                          onChange={(e) => setForceCompleteReason(e.target.value)}
                          className="min-h-[80px]"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Needs Resolution - NO override */}
              {completionBlockedInfo.type === 'needs_resolution' && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {completionBlockedInfo.message}
                  </p>
                  <div className="mt-3 text-sm text-red-700 dark:text-red-300">
                    <p className="font-medium">What needs to be done:</p>
                    <p className="mt-1">{completionBlockedInfo.resolutionNeeded}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowCompletionBlockedDialog(false);
                setCompletionBlockedInfo(null);
                setForceCompleteReason('');
              }}
            >
              Close
            </Button>
            {completionBlockedInfo?.canOverride && (
              <Button
                onClick={handleForceComplete}
                disabled={actionLoading || !forceCompleteReason.trim()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Override & Complete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Queue Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Generate Queue Report
            </DialogTitle>
            <DialogDescription>
              Export check-in queue data for the selected date range.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="report-start-date">Start Date</Label>
                <Input
                  id="report-start-date"
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  data-testid="queue-report-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-end-date">End Date</Label>
                <Input
                  id="report-end-date"
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  data-testid="queue-report-end-date"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                  setReportStartDate(firstDay.toISOString().split('T')[0]);
                  setReportEndDate(lastDay.toISOString().split('T')[0]);
                }}
                data-testid="queue-report-current-month-btn"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Current Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                  const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
                  setReportStartDate(firstDay.toISOString().split('T')[0]);
                  setReportEndDate(lastDay.toISOString().split('T')[0]);
                }}
                data-testid="queue-report-last-month-btn"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Last Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setReportStartDate(today);
                  setReportEndDate(today);
                }}
                data-testid="queue-report-today-btn"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Today
              </Button>
            </div>
            <div className="text-sm text-muted-foreground">
              Location: <span className="font-medium">{activeLocationTab === 'fp-nashville' ? 'Nashville' : 'Charlotte'}</span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowReportDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!reportStartDate || !reportEndDate) {
                  toast.error('Please select both start and end dates');
                  return;
                }
                if (new Date(reportStartDate) > new Date(reportEndDate)) {
                  toast.error('Start date must be before end date');
                  return;
                }
                setReportLoading(true);
                try {
                  const params = new URLSearchParams();
                  params.append('location_id', activeLocationTab);
                  params.append('start_date', reportStartDate);
                  params.append('end_date', reportEndDate);
                  params.append('format', 'csv');
                  
                  const response = await axios.get(`${API}/food-pantry/report/export?${params.toString()}`, {
                    responseType: 'blob'
                  });
                  
                  const blob = new Blob([response.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const locationName = activeLocationTab === 'fp-nashville' ? 'Nashville' : 'Charlotte';
                  a.download = `${locationName}-Queue-Report-${reportStartDate}-to-${reportEndDate}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  
                  toast.success('Queue report exported successfully');
                  setShowReportDialog(false);
                } catch (error) {
                  console.error('Report export error:', error);
                  toast.error(error.response?.data?.detail || 'Failed to export report');
                } finally {
                  setReportLoading(false);
                }
              }}
              disabled={reportLoading || !reportStartDate || !reportEndDate}
              data-testid="queue-report-export-btn"
            >
              {reportLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Participant Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link to Existing Participant
            </DialogTitle>
            <DialogDescription>
              Search for and select the correct participant to link to this check-in.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search Input */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, phone, or SSN..."
                  value={participantSearchQuery}
                  onChange={(e) => setParticipantSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchParticipants()}
                  className="pl-10"
                  data-testid="link-participant-search-input"
                />
              </div>
              <Button 
                onClick={handleSearchParticipants} 
                disabled={searchingParticipants || !participantSearchQuery.trim()}
                data-testid="link-participant-search-btn"
              >
                {searchingParticipants ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Search Results */}
            {participantSearchResults.length > 0 && (
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {participantSearchResults.map((participant) => (
                  <div
                    key={participant.id}
                    className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedParticipantToLink?.id === participant.id ? 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-l-purple-500' : ''
                    }`}
                    onClick={() => setSelectedParticipantToLink(participant)}
                    data-testid={`link-participant-result-${participant.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{participant.full_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {participant.date_of_birth && `DOB: ${participant.date_of_birth}`}
                          {participant.phone && ` • ${participant.phone}`}
                        </p>
                      </div>
                      {selectedParticipantToLink?.id === participant.id && (
                        <CheckCircle className="h-5 w-5 text-purple-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {participantSearchResults.length === 0 && participantSearchQuery && !searchingParticipants && (
              <div className="text-center py-8 text-muted-foreground">
                <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No participants found matching "{participantSearchQuery}"</p>
                <p className="text-sm">Try a different search term</p>
              </div>
            )}

            {/* Selected Participant Preview */}
            {selectedParticipantToLink && (
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700 font-medium mb-2">Selected Participant:</p>
                <p className="font-semibold">{selectedParticipantToLink.full_name}</p>
                {selectedParticipantToLink.date_of_birth && (
                  <p className="text-sm text-muted-foreground">DOB: {selectedParticipantToLink.date_of_birth}</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
              disabled={linkingParticipant}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkParticipant}
              disabled={linkingParticipant || !selectedParticipantToLink}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="link-participant-confirm-btn"
            >
              {linkingParticipant ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link Participant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny Verification Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ThumbsDown className="h-5 w-5" />
              Deny Check-In
            </DialogTitle>
            <DialogDescription>
              This will mark the check-in as ineligible. No case note will be created.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="denial-reason">Reason for Denial (Optional)</Label>
              <Textarea
                id="denial-reason"
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                placeholder="Enter the reason for denying this check-in..."
                rows={3}
                data-testid="denial-reason-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDenyDialog(false)}
              disabled={denyLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDenyVerification}
              disabled={denyLoading}
              data-testid="confirm-deny-btn"
            >
              {denyLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Denying...
                </>
              ) : (
                <>
                  <ThumbsDown className="h-4 w-4 mr-2" />
                  Deny Check-In
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Client Type Dialog */}
      <Dialog open={showChangeTypeDialog} onOpenChange={(open) => {
        setShowChangeTypeDialog(open);
        if (!open) setChangeTypeError(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Change Client Type
            </DialogTitle>
            <DialogDescription>
              Change how this check-in is processed. The current type is: <strong>{selectedCheckin?.client_type === 'new' ? 'New Client' : 'Returning Client'}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {changeTypeError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Cannot Change Type</p>
                    <p className="text-sm text-red-700 mt-1">{changeTypeError}</p>
                  </div>
                </div>
              </div>
            )}

            {selectedCheckin?.client_type === 'new' ? (
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Converting to Returning Client</strong> will:
                  </p>
                  <ul className="list-disc list-inside text-sm text-blue-700 mt-2 space-y-1">
                    <li>Search for a matching participant record</li>
                    <li>Verify 30-day eligibility window</li>
                    <li>Check Mon-Wed service day rules</li>
                    <li>Create automatic distribution case note if eligible</li>
                  </ul>
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleChangeClientType('returning')}
                  disabled={changeTypeLoading}
                  data-testid="btn-convert-to-returning"
                >
                  {changeTypeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying Eligibility...
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Convert to Returning Client
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>Converting to New Client</strong> will:
                  </p>
                  <ul className="list-disc list-inside text-sm text-purple-700 mt-2 space-y-1">
                    <li>Remove participant matching</li>
                    <li>Clear eligibility information</li>
                    <li>Treat as new intake requiring registration</li>
                  </ul>
                </div>
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={() => handleChangeClientType('new')}
                  disabled={changeTypeLoading}
                  data-testid="btn-convert-to-new"
                >
                  {changeTypeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Converting...
                    </>
                  ) : (
                    <>
                      <User className="h-4 w-4 mr-2" />
                      Convert to New Client
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowChangeTypeDialog(false)}
              disabled={changeTypeLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodPantryQueuePage;
